import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useFormContext } from 'react-hook-form';

import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Radio,
  RadioGroup,
} from '@brand/components';
import {
  IFieldProps,
  IRadioFieldSchema,
  IRadioFieldStyles,
} from 'schema/generator/FormSchema';

import { useErrorMessage } from 'hooks/form/useErrorMessage';
import { useStyles } from 'hooks/form/useStyles';

import ComponentIsVisible from 'components/utils/IsVisible';

export const radioFieldStyles: IRadioFieldStyles = {
  radioFlex: {
    columnGap: '33%',
  },
  radio: {
    size: 'md',
  },
};

const RadioField = ({
  defaultValue,
  field,
  id,
  name,
}: IFieldProps<IRadioFieldSchema>): JSX.Element => {
  const { label, isRequired, shouldDisplay, styles = {}, value } = field;

  const { t } = useTranslation('form');
  const { register, watch } = useFormContext();
  const fieldStyles = useStyles<IRadioFieldStyles>('radioField', styles);
  const errorMessage = useErrorMessage(name, label);

  const values = watch(name);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  return (
    <ComponentIsVisible when={isVisible}>
      <FormControl
        isInvalid={!!errorMessage}
        isRequired={isRequired}
        key={`${name}-control`}
        {...fieldStyles.control}
      >
        <ComponentIsVisible when={!!label}>
          <FormLabel htmlFor={name} variant="proRikApp" {...fieldStyles.label}>
            {label}
          </FormLabel>
        </ComponentIsVisible>

        <RadioGroup defaultValue={defaultValue || value} name={name}>
          <Flex {...fieldStyles.radioFlex}>
            {field.options.map(option => (
              <Radio
                data-testid={`${id}-${option.value}`}
                id={id}
                key={option.value}
                value={option.value}
                {...register(name)}
                {...fieldStyles.radio}
              >
                {option.label || option.value}
              </Radio>
            ))}
          </Flex>
        </RadioGroup>

        <ComponentIsVisible when={!!errorMessage}>
          <FormErrorMessage {...fieldStyles.errorMessage}>
            {t(`validations.${errorMessage as string}`)}
          </FormErrorMessage>
        </ComponentIsVisible>
      </FormControl>
    </ComponentIsVisible>
  );
};

export default RadioField;
